import React, { useState } from 'react';

const SECTIONS = [
  {
    title: 'Actions in Combat',
    rows: [
      ['Attack', 'One melee or ranged attack (more with Extra Attack)'],
      ['Melee Hit', 'Roll d20 + STR mod + proficiency vs target AC'],
      ['Ranged Hit', 'Roll d20 + DEX mod + proficiency vs target AC'],
      ['Finesse', 'Use STR or DEX for attack and damage (rapier, dagger, shortsword, etc.)'],
      ['Nat 20', 'Auto-hit. Roll all damage dice twice (crit)'],
      ['Nat 1', 'Auto-miss regardless of modifiers'],
      ['Ranged in Melee', 'Disadvantage on ranged attacks when a hostile is within 5 ft'],
      ['Long Range', 'Disadvantage on attacks beyond normal range, can\'t attack beyond max range'],
      ['Cast a Spell', 'Cast time of 1 action; may need concentration'],
      ['Dash', 'Double your movement for the turn'],
      ['Disengage', 'Your movement doesn\'t provoke opportunity attacks'],
      ['Dodge', 'Attacks against you have disadvantage, DEX saves have advantage'],
      ['Help', 'Give an ally advantage on their next ability check or attack'],
      ['Hide', 'Make a Stealth check; if you succeed, you\'re unseen'],
      ['Ready', 'Prepare an action with a trigger; uses your reaction'],
      ['Use an Object', 'Interact with a second object or something that needs an action'],
      ['Grapple', 'Athletics vs Athletics/Acrobatics; target is grappled (speed 0)'],
      ['Shove', 'Athletics vs Athletics/Acrobatics; push 5 ft or knock prone'],
    ],
  },
  {
    title: 'Bonus Actions',
    rows: [
      ['Off-hand Attack', 'Light weapon in each hand; no ability mod to damage'],
      ['Class Features', 'Cunning Action, Flurry of Blows, Healing Word, etc.'],
      ['Spells', 'If a spell says "bonus action" cast time'],
      ['Rule', 'Only one bonus action per turn; can\'t also cast a leveled spell as action'],
    ],
  },
  {
    title: 'Reactions',
    rows: [
      ['Opportunity Attack', 'Enemy leaves your reach without Disengaging'],
      ['Readied Action', 'Trigger you set on your turn happens'],
      ['Shield / Counterspell', 'Specific spells that use your reaction'],
      ['Rule', 'One reaction per round; resets at the start of your turn'],
    ],
  },
  {
    title: 'Movement',
    rows: [
      ['Normal', 'Use your speed in feet (usually 30 ft)'],
      ['Difficult Terrain', 'Costs 2 ft of movement per 1 ft moved'],
      ['Standing Up', 'Costs half your movement speed'],
      ['Crawling', 'Every foot costs 1 extra foot of movement'],
      ['Jumping (Long)', 'STR score in feet (running start) or half (standing)'],
      ['Jumping (High)', 'STR mod + 3 feet (running start) or half (standing)'],
      ['Climbing / Swimming', 'Costs 2 ft per 1 ft (or 1 ft if you have a climb/swim speed)'],
      ['Squeezing', 'One size smaller space; costs 2 ft per 1 ft; attacks have disadvantage'],
    ],
  },
  {
    title: 'Conditions',
    rows: [
      ['Blinded', 'Can\'t see. Auto-fail sight checks. Attacks have disadvantage, attacks against have advantage'],
      ['Charmed', 'Can\'t attack the charmer. Charmer has advantage on social checks'],
      ['Deafened', 'Can\'t hear. Auto-fail hearing checks'],
      ['Frightened', 'Disadvantage on checks/attacks while source is in sight. Can\'t willingly move closer'],
      ['Grappled', 'Speed is 0. Ends if grappler is incapacitated or effect moves you out of reach'],
      ['Incapacitated', 'Can\'t take actions or reactions'],
      ['Invisible', 'Impossible to see without magic/special sense. Advantage on attacks, attacks against have disadvantage'],
      ['Paralyzed', 'Incapacitated, can\'t move or speak. Auto-fail STR/DEX saves. Attacks have advantage. Melee hits are auto-crits'],
      ['Petrified', 'Turned to stone. Weight x10. Incapacitated, can\'t move/speak. Resistance to all damage. Immune to poison/disease'],
      ['Poisoned', 'Disadvantage on attack rolls and ability checks'],
      ['Prone', 'Can only crawl. Disadvantage on attacks. Melee attacks against have advantage, ranged have disadvantage. Stand up costs half movement'],
      ['Restrained', 'Speed 0. Attacks have disadvantage. Attacks against have advantage. Disadvantage on DEX saves'],
      ['Stunned', 'Incapacitated, can\'t move, can only speak falteringly. Auto-fail STR/DEX saves. Attacks against have advantage'],
      ['Unconscious', 'Incapacitated, can\'t move or speak, drops what it\'s holding, falls prone. Auto-fail STR/DEX saves. Attacks have advantage. Melee hits are auto-crits'],
      ['Exhaustion 1', 'Disadvantage on ability checks'],
      ['Exhaustion 2', 'Speed halved'],
      ['Exhaustion 3', 'Disadvantage on attacks and saves'],
      ['Exhaustion 4', 'HP maximum halved'],
      ['Exhaustion 5', 'Speed reduced to 0'],
      ['Exhaustion 6', 'Death'],
    ],
  },
  {
    title: 'Cover',
    rows: [
      ['Half Cover', '+2 AC and DEX saves (low wall, furniture, another creature)'],
      ['Three-Quarters', '+5 AC and DEX saves (portcullis, arrow slit, thick tree)'],
      ['Full Cover', 'Can\'t be targeted directly (completely concealed)'],
    ],
  },
  {
    title: 'Death & Healing',
    rows: [
      ['Death Saves', 'At 0 HP, roll d20 at start of turn. 10+ = success, 9- = fail. 3 successes = stable. 3 failures = dead'],
      ['Nat 1', 'Counts as 2 death save failures'],
      ['Nat 20', 'Regain 1 HP and wake up'],
      ['Damage at 0 HP', 'Each hit = 1 death fail. Crit = 2 fails. Massive damage (remaining >= max HP) = instant death'],
      ['Stabilize', 'Medicine DC 10 or Spare the Dying. Stable at 0 HP, unconscious. Regain 1 HP in 1d4 hours'],
      ['Healing at 0 HP', 'Any healing brings you back. You\'re conscious with that many HP'],
    ],
  },
  {
    title: 'Concentration',
    rows: [
      ['One Spell', 'Can only concentrate on one spell at a time'],
      ['Taking Damage', 'CON save DC = 10 or half the damage taken, whichever is higher'],
      ['Broken By', 'Failing the save, casting another concentration spell, being incapacitated, or dying'],
      ['No Action', 'Maintaining concentration doesn\'t cost an action'],
    ],
  },
  {
    title: 'Skill Check DCs',
    rows: [
      ['Very Easy', 'DC 5'],
      ['Easy', 'DC 10'],
      ['Medium', 'DC 15'],
      ['Hard', 'DC 20'],
      ['Very Hard', 'DC 25'],
      ['Nearly Impossible', 'DC 30'],
    ],
  },
  {
    title: 'Advantage & Disadvantage',
    rows: [
      ['Advantage', 'Roll 2d20, take the higher'],
      ['Disadvantage', 'Roll 2d20, take the lower'],
      ['Stacking', 'Multiple sources don\'t stack — still just 2d20'],
      ['Both at Once', 'If you have both advantage and disadvantage, they cancel out — roll normally'],
    ],
  },
  {
    title: 'Light & Vision',
    rows: [
      ['Bright Light', 'Normal vision for everyone'],
      ['Dim Light', 'Lightly obscured — disadvantage on Perception (sight)'],
      ['Darkness', 'Heavily obscured — effectively blinded'],
      ['Darkvision', 'See in darkness as if dim light (usually 60 ft). Can\'t discern color'],
      ['Torch / Light', 'Bright light 20 ft, dim light 20 ft beyond that'],
    ],
  },
  {
    title: 'Encounter Building',
    rows: [
      ['Easy', '1x party threshold — barely a challenge, no resource drain'],
      ['Medium', '2x party threshold — a fair fight, might use some resources'],
      ['Hard', '3x party threshold — could go badly, someone might drop'],
      ['Deadly', '4x party threshold — serious risk of death'],
      ['Multiplier', '3-6 monsters = x2, 7-10 = x2.5, 11-14 = x3, 15+ = x4'],
    ],
  },
  {
    title: 'Common Object ACs & HP',
    rows: [
      ['Paper / Cloth', 'AC 11, HP 1-2'],
      ['Rope / Leather', 'AC 11, HP 5'],
      ['Wood', 'AC 15, HP 10-20'],
      ['Stone', 'AC 17, HP 27'],
      ['Iron / Steel', 'AC 19, HP 18-27'],
      ['Adamantine', 'AC 23, HP 30+'],
    ],
  },
  {
    title: 'Spellcasting Quick Ref',
    rows: [
      ['Spell Attack', 'Roll d20 + spellcasting mod + proficiency vs AC'],
      ['Spell Save DC', '8 + spellcasting mod + proficiency'],
      ['Components', 'V = verbal, S = somatic (free hand), M = material (focus or pouch)'],
      ['Ritual Casting', 'Takes 10 extra minutes, doesn\'t spend a spell slot'],
      ['Counterspell', 'Reaction, 60 ft. Auto-counters same level or lower. Higher level = ability check DC 10 + spell level'],
      ['Dispel Magic', 'Auto-dispels 3rd level or lower. Higher = ability check DC 10 + spell level'],
    ],
  },
];

function Section({ title, rows }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={s.section}>
      <button style={s.sectionBtn} onClick={() => setOpen((v) => !v)}>
        <span>{title}</span>
        <span style={s.chevron}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={s.table}>
          {rows.map(([label, desc], i) => (
            <div key={i} style={{ ...s.row, ...(label === 'Rule' ? s.ruleRow : {}) }}>
              <span style={label === 'Rule' ? s.ruleLabel : s.label}>{label}</span>
              <span style={s.desc}>{desc}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DmCheatSheet() {
  const [expandAll, setExpandAll] = useState(false);

  return (
    <div style={s.wrapper}>
      <div style={s.header}>
        <span style={s.headerTitle}>DM Quick Reference</span>
        <button style={s.expandBtn} onClick={() => setExpandAll((v) => !v)}>
          {expandAll ? 'Collapse All' : 'Expand All'}
        </button>
      </div>
      <div style={s.scroll}>
        {SECTIONS.map((sec) => (
          <SectionControlled key={sec.title} title={sec.title} rows={sec.rows} forceOpen={expandAll} />
        ))}
      </div>
    </div>
  );
}

function SectionControlled({ title, rows, forceOpen }) {
  const [manualOpen, setManualOpen] = useState(false);
  const isOpen = forceOpen || manualOpen;

  return (
    <div style={s.section}>
      <button style={s.sectionBtn} onClick={() => setManualOpen((v) => !v)}>
        <span>{title}</span>
        <span style={s.chevron}>{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && (
        <div style={s.table}>
          {rows.map(([label, desc], i) => (
            <div key={i} style={{ ...s.row, ...(label === 'Rule' ? s.ruleRow : {}) }}>
              <span style={label === 'Rule' ? s.ruleLabel : s.label}>{label}</span>
              <span style={s.desc}>{desc}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  wrapper:     { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #4a3d28', flexShrink: 0 },
  headerTitle: { fontSize: 14, fontWeight: 700, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.04em' },
  expandBtn:   { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 11, padding: '4px 10px', cursor: 'pointer' },
  scroll:      { flex: 1, overflowY: 'auto', padding: '4px 0' },
  section:     { borderBottom: '1px solid #2a2040' },
  sectionBtn:  { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'transparent', border: 'none', color: '#c0b090', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Press Start 2P', monospace" },
  chevron:     { fontSize: 10, color: '#6a5d45' },
  table:       { padding: '0 14px 10px' },
  row:         { display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid rgba(74,61,40,0.3)' },
  ruleRow:     { background: 'rgba(212,168,71,0.06)', borderRadius: 4, padding: '5px 6px', marginTop: 2 },
  label:       { minWidth: 110, maxWidth: 130, flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#d4a847' },
  ruleLabel:   { minWidth: 110, maxWidth: 130, flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#e0a050', fontStyle: 'italic' },
  desc:        { flex: 1, fontSize: 12, color: '#c8bca0', lineHeight: '1.45' },
};
